import React, { useCallback, useEffect, useState } from "react";
import "card-pattern/dist/main.css";
import "./Members.css";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import {
  fetchTeamMembers,
  fetchDefaultMembers,
  fetchManualMembers,
  fetchMembershipSummary,
  fetchMemberAccess,
  clearManualMembers,
  fetchMemberRoleOptions,
  resetSearch,
} from "../../../store/slices/memberSlice";
import Loader from "../../../components/common/Loader";
import Notifications from "../../../components/common/Notifications";
import CardTable from "../../../components/common/CardTable";
import NoResultsFound from "../../../components/common/NoResultsFound";
import CommonErrorBanner from "../../../components/common/CommonErrorBanner";
import Table, { TableRow } from "../../../components/common/Table/Table";
import Pagination from "../../../components/common/Table/Pagination";
import Tabs from "../../../components/common/Tabs";
import { TabData } from "../../../config/tabsConfig";
import { useNavigate, useLocation, Link } from "react-router-dom";
import {
  columnsDetail,
  columnsInfo,
  columnsSummary,
  defaultedMembersColumns,
} from "./membersColumns";
import { safeOpenMailClient } from "./membersHelpers";
import { teamStorage } from "../../../utils/teamStorage";
import DefaultedMembersTab from "./DefaultedMembersTab";
import ManuallyAddedMembersTab from "./ManuallyAddedMembersTab";
import AddMembersTab from "./AddMembersTab";
import SponsorCard from "./SponsorCard";
import BreadCrumbs from "../../../components/common/BreadCrumbs";


// Re-export helpers for backward compatibility
export {
  parseEntryNames,
  buildMailtoHref,
  normalizeEmailAddresses,
  safeOpenMailClient,
  filterEmails,
} from "./membersHelpers";

const TabPlaceholder: React.FC = () => <div />;

const Members: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { loading } = useAppSelector((state) => state.profile);
  const profile = useAppSelector((state: any) => state.profile?.profile);

  // Get members data from Redux store
  const {
    membersList: members,
    teamDetails,
    sponsorsList,
    defaultMembers: defaultMembersFromAPI,
    defaultMembersTotal,
    manualMembers: manualMembersFromAPI,
    manualMembersTotal,
    membershipSummary,
    loading: membersLoading,
    memberAccess,
  } = useAppSelector((state) => state.membersSlice);

  const teamMembersError = useAppSelector(
    (state) => state.membersSlice.teamMembersError,
  );
  const teamMembersStatus = useAppSelector(
    (state) => state.membersSlice.teamMembersStatus,
  );
  const defaultMembersError = useAppSelector(
    (state) => state.membersSlice.defaultMembersError,
  );
  const manualMembersError = useAppSelector(
    (state) => state.membersSlice.manualMembersError,
  );
  const membershipSummaryError = useAppSelector(
    (state) => state.membersSlice.membershipSummaryError,
  );

  // Check if team not found (404 error)
  const isTeamNotFound = teamMembersStatus === 404;

  // Show single banner if 2 or more APIs failed (not individual per-table banners)
  const errorCount = [
    teamMembersError,
    defaultMembersError,
    manualMembersError,
    membershipSummaryError,
  ].filter(Boolean).length;
  const showSingleBanner = errorCount >= 2;

  const stateTeamId = (location.state as { teamId?: string | number } | null)?.teamId;
  const teamId: string | undefined =
    stateTeamId == null
      ? teamStorage.getTeamId() ?? undefined
      : String(stateTeamId);

  // Track which tab is active to fetch data on demand
  const [activeTabId, setActiveTabId] = useState<number>(1);

  // determine if current user is an admin
  const isAdmin = profile?.isAdmin;
  const normalizedMemberAccess =
    (memberAccess as any)?.data ??
    (memberAccess as any)?.access ??
    memberAccess;
  const canEditTeam = Boolean(
    normalizedMemberAccess?.canEdit ??
      normalizedMemberAccess?.canMemberShipEdit ??
      false,
  );
  const canMemberShipEditTeam = Boolean(
    normalizedMemberAccess?.canMemberShipEdit ??
      normalizedMemberAccess?.canMembershipEdit ??
      false,
  );

  const handleEditTeam = (selectedRow: any) => {
    navigate("/home/add-profile", {
      state: { from: "members", teamID: selectedRow.id },
    });
  };

  // pagination state for each tab
  const [defaultPage, setDefaultPage] = useState<number>(1);
  const [manualPage, setManualPage] = useState<number>(1);
  const [addPage, setAddPage] = useState<number>(1);
  const [glPage, setGlPage] = useState<number>(1);
  const glItemsPerPage = 10;
  const itemsPerPage = 10;

  // Track initial load to avoid fetching on mount
  const isInitialLoadRef = React.useRef(true);

  // Sorting state for default members
  const [defaultSortColumn, setDefaultSortColumn] =
    useState<string>("lastName");
  const [defaultSortDirection, setDefaultSortDirection] = useState<
    "asc" | "desc"
  >("asc");

  // Sorting state for manual members
  const [manualSortColumn, setManualSortColumn] = useState<string>("lastName");
  const [manualSortDirection, setManualSortDirection] = useState<
    "asc" | "desc"
  >("asc");
  // Build tabs dynamically based on data availability
  const tabsInfo: TabData[] = React.useMemo(() => {
    const tabs: TabData[] = [];
    let nextId = 1;

    // Tab 1: Defaulted Members - only add if there's data and default membership is not explicitly 'No'
    const teamDefaultMembership = teamDetails?.defaultMembership;
    if (
      teamDefaultMembership !== "No" &&
      defaultMembersFromAPI &&
      defaultMembersFromAPI.length > 0
    ) {
      tabs.push({
        id: nextId++,
        title: "Defaulted Members",
        component: TabPlaceholder as any,
      });
    }

    // Tab 2: Manually added members - only add if there's data
    if (manualMembersFromAPI && manualMembersFromAPI.length > 0) {
      tabs.push({
        id: nextId++,
        title: "Manually added members",
        component: TabPlaceholder as any,
      });
    }

    // Tab 3: Add members - only when membership edit permission is granted
    if (canMemberShipEditTeam) {
      tabs.push({
        id: nextId++,
        title: "Add members",
        component: TabPlaceholder as any,
      });
    }

    return tabs;
  }, [
    defaultMembersFromAPI,
    manualMembersFromAPI,
    teamDetails?.defaultMembership,
    canMemberShipEditTeam,
  ]);

  // Sync activeTabId to first available tab if current tab is not available or when tabs change
  useEffect(() => {
    if (tabsInfo.length > 0) {
      // If current active tab doesn't exist in available tabs, switch to first available
      if (!tabsInfo.some((t) => t.id === activeTabId)) {
        setActiveTabId(tabsInfo[0].id);
      }
    }
  }, [tabsInfo, activeTabId]);

  // Initial load - fetch team members, default members, and manual members
  useEffect(() => {
    if (teamId) {
      // clear manual members from previous team so next tab click fetches fresh data
      dispatch(clearManualMembers());
      // clear add-members search so it does not persist across page entries
      dispatch(resetSearch());
      // reset UI state - don't hardcode tab 1, let it be set by tabsInfo sync
      setManualPage(1);

      dispatch(fetchTeamMembers({ teamId }));

      // Mark initial load as complete after first render
      isInitialLoadRef.current = false;
    }
  }, [dispatch, teamId]);

  // Fetch member access separately so profile/nuid updates do not reset member tabs/data.
  useEffect(() => {
    if (teamId) {
      dispatch(
        fetchMemberAccess({
          teamId,
          nuid: profile?.nuid ? String(profile.nuid) : null,
        }),
      );
    }
  }, [dispatch, teamId, profile?.nuid]);

  // Only fetch other APIs if team members was successful (not 404)
  useEffect(() => {
    if (teamId && !isTeamNotFound && !teamMembersError) {
      dispatch(
        fetchDefaultMembers({
          teamId,
          sortColumn: "LAST_NAME",
          sortOrder: "ASC",
          page: 1,
          per_page: itemsPerPage,
        }),
      );
      dispatch(
        fetchManualMembers({
          teamId,
          sortColumn: "LAST_NAME",
          sortOrder: "ASC",
          page: 1,
          per_page: itemsPerPage,
        }),
      );
      // Also fetch team-member role status options for both tabs
      dispatch({ type: "members/fetchMemberRoleOptions/pending" });
      dispatch<any>(fetchMemberRoleOptions({ member: "default" }));
      dispatch<any>(fetchMemberRoleOptions({ member: "manual" }));
    }
  }, [dispatch, teamId, isTeamNotFound, teamMembersError]);

  /**
   * Re-fetches the UBT Membership Summary from the API so that counts
   * (Manually Added, Defaulted role buckets, Total) reflect the latest
   * state without requiring a full page refresh.
   * Called after a member is added or a Default member's team role is changed.
   */
  const refreshMembershipSummary = React.useCallback(() => {
    if (!teamId || !teamDetails?.defaultMembership || isTeamNotFound) return;
    const defaultMembershipValue =
      teamDetails.defaultMembership === "Yes" ? "Y" : "N";
    dispatch(
      fetchMembershipSummary({
        teamId,
        defaultMembership: defaultMembershipValue,
      }),
    );
  }, [dispatch, teamId, teamDetails?.defaultMembership, isTeamNotFound]);

  // Fetch membership summary when teamDetails.defaultMembership is available
  useEffect(() => {
    if (teamId && teamDetails?.defaultMembership && !isTeamNotFound) {
      // Extract Y or N from the formatted value ("Yes" or "No")
      const defaultMembershipValue =
        teamDetails.defaultMembership === "Yes" ? "Y" : "N";
      dispatch(
        fetchMembershipSummary({
          teamId,
          defaultMembership: defaultMembershipValue,
        }),
      );
    }
  }, [dispatch, teamId, teamDetails?.defaultMembership, isTeamNotFound]);

  // Fetch default members when page changes
  useEffect(() => {
    // Skip if initial mount
    if (isInitialLoadRef.current) {
      return;
    }

    const defaultTab = tabsInfo.find((t) => t.title === "Defaulted Members");
    if (teamId && activeTabId === defaultTab?.id) {
      const apiSortColumn =
        defaultSortColumn === "lastName" ? "LAST_NAME" : "FIRST_NAME";
      const apiSortOrder = defaultSortDirection.toUpperCase() as "ASC" | "DESC";
      dispatch(
        fetchDefaultMembers({
          teamId,
          sortColumn: apiSortColumn,
          sortOrder: apiSortOrder,
          page: defaultPage,
          per_page: itemsPerPage,
        }),
      );
    }
  }, [
    dispatch,
    teamId,
    defaultPage,
    defaultSortColumn,
    defaultSortDirection,
    activeTabId,
  ]);

  // Fetch manual members when page changes
  useEffect(() => {
    // Skip if initial mount
    if (isInitialLoadRef.current) {
      return;
    }

    const manualTab = tabsInfo.find(
      (t) => t.title === "Manually added members",
    );
    if (teamId && activeTabId === manualTab?.id) {
      const apiSortColumn =
        manualSortColumn === "lastName" ? "LAST_NAME" : "FIRST_NAME";
      const apiSortOrder = manualSortDirection.toUpperCase() as "ASC" | "DESC";
      dispatch(
        fetchManualMembers({
          teamId,
          sortColumn: apiSortColumn,
          sortOrder: apiSortOrder,
          page: manualPage,
          per_page: itemsPerPage,
        }),
      );
    }
  }, [
    dispatch,
    teamId,
    manualPage,
    activeTabId,
    manualSortColumn,
    manualSortDirection,
  ]);

  const handleTabSwitch = React.useCallback((clickedTab: TabData) => {
    const tabTitle = clickedTab?.title;

    // Reset pagination to page 1 - useEffect will handle the API call
    if (tabTitle === "Defaulted Members") {
      setDefaultPage(1);
    } else if (tabTitle === "Manually added members") {
      setManualPage(1);
    } else if (tabTitle === "Add members") {
      setAddPage(1);
    }
  }, []);

  // Listen for clicks on the shared Tabs component buttons and update activeTabId
  React.useEffect(() => {
    // Delegated click handler: always fetch for Manual tab on click
    const handler = (e: Event) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const link = target.closest(".ds-tabs__link");
      if (!link) return;

      const buttons = Array.from(document.querySelectorAll(".ds-tabs__link"));
      const idx = buttons.indexOf(link);
      if (idx === -1) return;

      const id = tabsInfo[idx]?.id ?? idx + 1;
      const clickedTab = tabsInfo[idx];

      // Set active tab first for immediate UI feedback
      setActiveTabId(id);

      // Reset pagination to page 1 when switching tabs, but keep sort state
      handleTabSwitch(clickedTab);
    };

    // use capture phase so we receive the event even if propagation is stopped by Tabs internals
    document.addEventListener("click", handler, true);
    return () => document.removeEventListener("click", handler, true);
  }, [tabsInfo, handleTabSwitch]);

  const handleBreadcrumbPrevious = useCallback((): void => {
    navigate("/home");
  }, [navigate]);

  const ubtNameForBreadcrumb = (teamDetails?.teamName ?? "").trim();
  const truncatedUbtName =
    ubtNameForBreadcrumb.length > 25
      ? `${ubtNameForBreadcrumb.slice(0, 25)}...`
      : ubtNameForBreadcrumb;
  const projectsCrumbLabel = truncatedUbtName
    ? `Projects: ${truncatedUbtName}`
    : "Projects";

  // Navigate to UBTLandingPage if team not found
  React.useEffect(() => {
    if (isTeamNotFound) {
      navigate("/home");
    }
  }, [isTeamNotFound, navigate]);

  if (isTeamNotFound) {
    return null;
  }

  if (!teamId && !loading && !membersLoading) {
    return (
      <div className="min-h-[70vh] lg:mx-40! mx-5!">
        <div className="mb-8!">
          <BreadCrumbs
            items={[
              { label: 'UBT Home', path: '/home', onClick: handleBreadcrumbPrevious },
              { label: 'Members' },
            ]}
          />
        </div>
        <h1 className="h1 tablet:mt-8! mobile:mt-3.5!">Members</h1>
        <div className="mt-8! max-w-[80%]">
          <Notifications
            heading="Please select an UBT"
            variant="Alert"
            messageInfo="Please select an UBT before accessing Members page. You can access the page from Select UBT/ My Teams etc."
          >
            <ul className="flex items-start list-disc m-0 my-0! ml-4 mt-5!">
              <li>
                <Link
                  to="/home/select-ubt"
                  data-analytics-type="link"
                  className="text-kp-blue-dark underline text-left text-[16px]! w-full bg-transparent border-none p-0 cursor-pointer hover:text-kp-blue"
                  aria-label="Navigate to Select a UBT page"
                >
                  Select a UBT
                </Link>
              </li>
            </ul>
          </Notifications>
        </div>
      </div>
    );
  }

  if (loading || membersLoading) return <Loader />;

  const rowsDetail: TableRow[] = teamDetails
    ? [
        {
          rowKey: `detail-${teamDetails.teamId}`,
          id: teamDetails.teamId,
          ubtName: (
            <div className="max-w-[145px] whitespace-normal break-words overflow-wrap-[anywhere]">
              {teamDetails.teamName ?? ""}
            </div>
          ),
          dateTeamEstablished: teamDetails.teamDateCreated ?? "",
          teamRating: teamDetails.teamRating ?? "",
          serviceType: teamDetails.serviceDeliveryType ?? "",
          usingHuddles: teamDetails.usingHuddles ?? "",
          defaultMembership: teamDetails.defaultMembership ?? "",
          // show Edit button only for users with canEdit access
          edit: canEditTeam ? (
            <button
              className={`button py-0! -inverted hover:text-white!`}
              onClick={() => handleEditTeam({ id: teamDetails.teamId })}
              data-analytics-type="button"
              data-analytics-click="Default"
            >
              <img
                src="/assets/images/pencil-outline.svg"
                alt="Edit icon"
                className="h-6! w-6!"
              />
              <span>Edit</span>
            </button>
          ) : null,
        },
      ]
    : [];

  const rowsInfo: TableRow[] = [...(members as TableRow[])].map(
    (row, index) => ({
      ...row,
      rowKey: row.id ?? row.glString ?? `info-row-${index}`,
    }),
  );

  // Removed duplicate declaration of tabsInfo

  const openMailClient = (addresses?: string[], subject?: string) =>
    safeOpenMailClient(addresses, subject);

  // Use API data if available, otherwise use empty array
  const rowsSummary = Array.isArray(membershipSummary) ? membershipSummary : [];
  const rowsSummaryWithKeys = rowsSummary.map((row, index) => ({
    ...row,
    ubtMembershipSummary:
      row.ubtMembershipSummary === "Total" ? (
        <strong>Total</strong>
      ) : (
        row.ubtMembershipSummary
      ),
    rowKey: row.id ?? `summary-row-${index}`,
  }));

  return (
    <div className="min-h-[70vh] lg:mx-40! mx-5!">
      <div className="flex justify-between items-center">
        <div className="mobile-margin ">
          <div className="mb-20!">
          <BreadCrumbs
            items={[
              {
                label: 'UBT Home',
                path: '/home',
                onClick: handleBreadcrumbPrevious,
              },
              {
                label: `${projectsCrumbLabel}`,
                path: '/home/projects',
              },
              {
                label: 'Members',
              },
            ]}
          />
        </div>

        </div>
        <div className="mobile-margin">
          <div className="about-this-team" style={{ color: '#677083' }}> About this team :</div>
          <Link
            to="/home/path-to-performance"
            state={{ teamId: teamId, from: 'Members'}}
            data-analytics-type="link"
            className="no-underline! p1 -medium"
          >
            P2P
          </Link>
          <Link
            to="/home/projects"
            state={{ teamId: teamId, from: 'Members' }}
            data-analytics-type="link"
            className="no-underline! p1 -medium pl-6!"
          >
            Projects
          </Link>

          <Link
            to="/home/history"
            state={{ teamId: teamId, from: 'Members' }}
            data-analytics-type="link"
            className="no-underline! p1 -medium pl-6! "
          >
            History
          </Link>
        </div>
      </div>

      <div>
        <div className="flex justify-between items-end mb-4! md:mb-0! desktop: ml-4! margin-top-4u desktop:mt-12! md:mt-0! mobile:mt-3.5!">
          <div className="min-w-0 w-full">
            <h1 className="h1 tablet:mt-8! mobile:mt-3.5!">Members</h1>
            <h2 className="h2 tablet:mt-8! mobile:mt-3.5! w-full max-w-full whitespace-normal break-all [overflow-wrap:anywhere]">{ubtNameForBreadcrumb}</h2>
          </div>
        </div>

        {!teamMembersError && (
          <div>
            <div className="sponsorsLaborSponsorsList">
              <div
                className="grid grid-cols-2 grid-rows-2 gap-x-6 gap-y-6 ml-5!"
                style={{
                  background: "var(--color-blue-5, #F2F8FB)",
                  padding: "24px 0",
                }}
              >
                <SponsorCard
                  key="sponsors"
                  keyName="Sponsors & Labor Sponsors"
                  title="Sponsors & Labor Sponsors"
                  fallbackMarginLeft={"33%"}
                  sponsorsList={sponsorsList}
                  openMailClient={openMailClient}
                  teamName={teamDetails?.teamName}
                />
                <SponsorCard
                  key="coleads"
                  keyName="Co-Leads & Labor Co-Leads"
                  title="Co-Leads & Labor Co-Leads"
                  justifySelf={"start"}
                  sponsorsList={sponsorsList}
                  openMailClient={openMailClient}
                  teamName={teamDetails?.teamName}
                />
                <SponsorCard
                  key="consultants"
                  keyName="Consultants"
                  title="Consultants"
                  fallbackMarginLeft={"22%"}
                  sponsorsList={sponsorsList}
                  openMailClient={openMailClient}
                  teamName={teamDetails?.teamName}
                />
                <SponsorCard
                  key="thwps"
                  keyName="TH/WPS Champion"
                  title="TH/WPS Champion"
                  justifySelf={"start"}
                  paddingLeft={"8"}
                  fallbackMarginLeft={"26%"}
                  sponsorsList={sponsorsList}
                  openMailClient={openMailClient}
                  teamName={teamDetails?.teamName}
                />
              </div>
            </div>
            <div>
              <p className="p1 -medium mt-10! mb-5! ml-5!">
                Detail about the selected unit based team
              </p>
            </div>
          </div>
        )}

        {showSingleBanner && (
          <div className="flex justify-center items-center min-h-[200px]">
            <CommonErrorBanner
              heading="Internal Server Error"
              messageInfo={
                "The server encountered an issue and was unable to complete your request.\nPlease try again later."
              }
            />
          </div>
        )}

        <div className="no-row-bg">
          <CardTable>
            {(() => {
              if (showSingleBanner) return null;

              if (teamMembersError) {
                return (
                  <div className="flex justify-center items-center min-h-[200px]">
                    <CommonErrorBanner
                      heading="Internal Server Error"
                      messageInfo={
                        "The server encountered an issue and was unable to complete your request.\nPlease try again later."
                      }
                    />
                  </div>
                );
              }

              return <Table columns={columnsDetail} rows={rowsDetail} />;
            })()}
          </CardTable>
        </div>

        <div className="no-row-bg tableInfo mt-6!">
          <CardTable className="table-gl-string">
            {(() => {
              if (showSingleBanner) return null;

              if (teamMembersError) {
                return (
                  <div className="flex justify-center items-center min-h-[200px]">
                    <CommonErrorBanner
                      heading="Internal Server Error"
                      messageInfo={
                        "The server encountered an issue and was unable to complete your request.\nPlease try again later."
                      }
                    />
                  </div>
                );
              }

              const displayRows =
                rowsInfo.length > glItemsPerPage
                  ? rowsInfo.slice(
                      (glPage - 1) * glItemsPerPage,
                      (glPage - 1) * glItemsPerPage + glItemsPerPage,
                    )
                  : rowsInfo;

              return <Table columns={columnsInfo} rows={displayRows} />;
            })()}
            {rowsInfo.length > glItemsPerPage && (
              <div
                className="mt-4 left-0 mobile:relative mobile:left-[30px]"
                data-testid="pagination-glstring-wrapper"
              >
                <Pagination
                  data-testid="pagination-glstring"
                  totalItems={rowsInfo.length}
                  itemsPerPage={glItemsPerPage}
                  currentPage={glPage}
                  onPageChange={(p: number) => setGlPage(p)}
                />
              </div>
            )}
          </CardTable>
        </div>

        <div className="mt-6!">
          <Tabs
            key={tabsInfo.map((t) => t.id).join("-")}
            tabs={tabsInfo}
            activeTabId={activeTabId}
            extraProps={{
              isAdmin,
              defaultMembers: defaultMembersFromAPI,
              defaultPage,
              setDefaultPage,
              totalItems: defaultMembersTotal,
              manuallyAddedMembers: manualMembersFromAPI,
              manualPage,
              setManualPage,
              manualTotalItems: manualMembersTotal,
              rowsInfo,
              addPage,
              setAddPage,
              itemsPerPage,
              columnsInfo,
              defaultedMembersColumns,
            }}
          />

          {/* Render the actual tab panels below the shared Tabs control so we control hook execution */}
          <div className="defaulted-manually-members">
            {tabsInfo.find(
              (t) => t.title === "Defaulted Members" && t.id === activeTabId,
            ) && (
              <DefaultedMembersTab
                defaultMembers={defaultMembersFromAPI}
                defaultPage={defaultPage}
                setDefaultPage={setDefaultPage}
                totalItems={defaultMembersTotal}
                showSingleBanner={showSingleBanner}
                sortColumn={defaultSortColumn}
                sortDirection={defaultSortDirection}
                setSortColumn={setDefaultSortColumn}
                setSortDirection={setDefaultSortDirection}
                teamId={teamId}
                onRoleUpdated={refreshMembershipSummary}
              />
            )}
            {tabsInfo.find(
              (t) =>
                t.title === "Manually added members" && t.id === activeTabId,
            ) && (
              <ManuallyAddedMembersTab
                manuallyAddedMembers={manualMembersFromAPI}
                manualPage={manualPage}
                setManualPage={setManualPage}
                itemsPerPage={itemsPerPage}
                manualTotalItems={manualMembersTotal}
                showSingleBanner={showSingleBanner}
                sortColumn={manualSortColumn}
                sortDirection={manualSortDirection}
                setSortColumn={setManualSortColumn}
                setSortDirection={setManualSortDirection}
                teamId={teamId}
                onRoleUpdated={refreshMembershipSummary}
              />
            )}
            {tabsInfo.find(
              (t) => t.title === "Add members" && t.id === activeTabId,
            ) && (
              <AddMembersTab
                isAdmin={isAdmin}
                manuallyAddedMembers={manualMembersFromAPI}
                manualPage={manualPage}
                setManualPage={setManualPage}
                itemsPerPage={itemsPerPage}
                manualTotalItems={manualMembersTotal}
                sortColumn={manualSortColumn}
                sortDirection={manualSortDirection}
                setSortColumn={setManualSortColumn}
                setSortDirection={setManualSortDirection}
                teamId={teamId}
                onSwitchToManualTab={() => {
                  // Refresh summary counts in real-time after a member is added
                  refreshMembershipSummary();
                  const manualTab = tabsInfo.find(
                    (t) => t.title === "Manually added members",
                  );
                  if (manualTab) {
                    setActiveTabId(manualTab.id);
                    dispatch(
                      fetchManualMembers({
                        teamId: teamId!,
                        sortColumn: "LAST_NAME",
                        sortOrder: "ASC",
                        page: 1,
                        per_page: itemsPerPage,
                      }),
                    );
                  } else {
                    // Tab doesn't exist yet — re-fetch manual members and switch to the tab when data arrives
                    dispatch(
                      fetchManualMembers({
                        teamId: teamId!,
                        sortColumn: "LAST_NAME",
                        sortOrder: "ASC",
                        page: 1,
                        per_page: itemsPerPage,
                      }),
                    ).then(() => {
                      // after fetch, tabsInfo will update via memo; find the manual tab and activate it
                      const manualTabAfterFetch = tabsInfo.find(
                        (t) => t.title === "Manually added members",
                      );
                      if (manualTabAfterFetch) {
                        setActiveTabId(manualTabAfterFetch.id);
                      }
                    });
                  }
                }}
              />
            )}
          </div>
        </div>

        <div className="no-row-bg tableInfo mt-9!">
          <CardTable>
            {(() => {
              if (showSingleBanner) return null;

              if (membershipSummaryError) {
                return (
                  <div className="flex justify-center items-center min-h-[200px]">
                    <CommonErrorBanner
                      heading="Internal Server Error"
                      messageInfo={
                        "The server encountered an issue and was unable to complete your request.\nPlease try again later."
                      }
                    />
                  </div>
                );
              }

              if (rowsSummaryWithKeys.length === 0) {
                return (
                  <div className="flex justify-center items-center min-h-[200px]">
                    <NoResultsFound
                      title="No membership summary found"
                      message="There is no membership summary available for this team."
                    />
                  </div>
                );
              }

              return (
                <Table columns={columnsSummary} rows={rowsSummaryWithKeys} />
              );
            })()}
          </CardTable>
        </div>
      </div>
    </div>
  );
};

export default Members;
